import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { submitRating } from '../../services/ratingService';
import { reportLostItem } from '../../services/lostItemService';
import { createTicket } from '../../services/supportService';
import { StarRating } from '../../components/safety/SafetyComponents';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import toast from 'react-hot-toast';

// ═══════════════ RATE TRIP ═══════════════
export function RateTrip() {
  const { bookingId } = useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isRider = userProfile?.role === 'rider';
  const homePath = isRider ? '/rider' : '/driver';

  // Validate review: must be either empty OR 20-500 chars
  const reviewLengthValid = review.length === 0 || (review.length >= 20 && review.length <= 500);
  const canSubmit = stars > 0 && reviewLengthValid && !submitting;

  const handleSubmit = async () => {
    if (stars === 0) { toast.error('Select a star rating'); return; }
    if (!reviewLengthValid) { toast.error('Review must be either empty or 20-500 characters'); return; }
    setSubmitting(true);
    try {
      await submitRating({
        tripId: bookingId, bookingId, raterId: userProfile.id, raterRole: userProfile.role,
        rateeId: null, rateeRole: isRider ? 'driver' : 'rider',
        stars, review: review || null,
      });
      toast.success('Rating submitted! Redirecting...');
      // Auto-navigate after 2 seconds
      setTimeout(() => {
        navigate(homePath);
      }, 2000);
    } catch (err) {
      toast.error(err.message);
      setSubmitting(false);
    }
  };

  // Helper text: shows minimum requirement only if user has typed under 20 chars
  const reviewHelpText = review.length > 0 && review.length < 20
    ? `${20 - review.length} more characters needed`
    : review.length >= 20
    ? `${review.length}/500`
    : null;

  return (
    <div className="max-w-[500px] mx-auto p-6">
      {/* Back to home/dashboard */}
      <div className="mb-4">
        <button
          onClick={() => navigate(homePath)}
          className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0"
        >
          ← {isRider ? 'Home' : 'Dashboard'}
        </button>
      </div>

      <h1 className="font-serif mt-0 mb-2 text-center">Rate Your Trip</h1>
      <p className="text-center text-body mb-8">How was your experience?</p>

      <div className="flex justify-center mb-8">
        <StarRating value={stars} onChange={setStars} size={48} />
      </div>

      <div className="form-group">
        <label className="form-label">Review (optional)</label>
        <textarea
          className="form-input"
          rows={4}
          value={review}
          onChange={(e) => setReview(e.target.value)}
          placeholder="Optional. If provided, must be 20-500 characters."
          maxLength={500}
        />
        {reviewHelpText && (
          <div className="text-[0.8rem] text-right mt-1"
            style={{ color: review.length > 0 && review.length < 20 ? '#ff6b6b' : '#94a3b8' }}>
            {reviewHelpText}
          </div>
        )}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="btn btn-primary w-full mt-4"
        style={{ opacity: canSubmit ? 1 : 0.5 }}
      >
        {submitting ? 'Submitting...' : 'Submit Rating'}
      </button>

      <BackButtonBottom to={homePath} label={isRider ? 'Home' : 'Dashboard'} />
    </div>
  );
}

// ═══════════════ REPORT LOST ITEM ═══════════════
export function ReportLostItem() {
  const { bookingId } = useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('back_seat');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!description || description.length < 10) { toast.error('Describe the item (min 10 chars)'); return; }
    setSubmitting(true);
    try {
      const result = await reportLostItem({
        tripId: bookingId, bookingId, riderId: userProfile.id,
        description, locationInVehicle: location, photos: [],
      });
      toast.success(`Lost item reported! Ref: ${result.referenceNumber}`);
      navigate(-1);
    } catch (err) { toast.error(err.message); }
    setSubmitting(false);
  };

  return (
    <div className="max-w-[500px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Back</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Report Lost Item</h1>
      <p className="text-body mb-6">Describe what you left in the vehicle. The driver and admin will be notified.</p>

      <div className="form-group">
        <label className="form-label">What did you lose? *</label>
        <textarea className="form-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Black iPhone 14 Pro in leather case" maxLength={500} />
      </div>

      <div className="form-group">
        <label className="form-label">Where in the vehicle?</label>
        <select className="form-input" value={location} onChange={(e) => setLocation(e.target.value)}>
          <option value="back_seat">Back seat</option>
          <option value="front_seat">Front seat</option>
          <option value="boot">Boot / trunk</option>
          <option value="door_pocket">Door pocket</option>
          <option value="floor">Floor</option>
          <option value="unsure">Not sure</option>
        </select>
      </div>

      <button onClick={handleSubmit} disabled={submitting} className="btn btn-primary w-full mt-4">
        {submitting ? 'Submitting...' : 'Report Lost Item'}
      </button>

      <BackButtonBottom label="Back" />
    </div>
  );
}

// ═══════════════ SUPPORT TICKET ═══════════════
export function SupportTicket() {
  const { bookingId } = useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    { value: 'billing', label: 'Billing & Payment' },
    { value: 'safety', label: 'Safety Concern' },
    { value: 'driver_behaviour', label: 'Driver Behaviour' },
    { value: 'lost_item', label: 'Lost Item' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'other', label: 'Other' },
  ];

  const handleSubmit = async () => {
    if (!category) { toast.error('Select a category'); return; }
    if (description.length < 50 || description.length > 2000) { toast.error('Description must be 50-2000 characters'); return; }
    setSubmitting(true);
    try {
      const result = await createTicket({
        tripId: bookingId || null, bookingId: bookingId || null,
        submittedBy: userProfile.id, submitterRole: userProfile.role,
        category, description, attachments: [],
      });
      toast.success(`Ticket created! Ref: ${result.referenceNumber}`);
      navigate(-1);
    } catch (err) { toast.error(err.message); }
    setSubmitting(false);
  };

  return (
    <div className="max-w-[500px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Back</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Create Support Ticket</h1>
      <p className="text-body mb-6">We'll get back to you within 24 hours (2 hours for safety issues).</p>

      <div className="form-group">
        <label className="form-label">Category *</label>
        <select className="form-input" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Select category...</option>
          {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Description * (50-2000 characters)</label>
        <textarea className="form-input" rows={5} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your issue in detail..." maxLength={2000} />
        <div className="text-[0.8rem] text-muted text-right">{description.length}/2000</div>
      </div>

      <button onClick={handleSubmit} disabled={submitting || !category} className="btn btn-primary w-full mt-4">
        {submitting ? 'Creating...' : 'Submit Ticket'}
      </button>

      <BackButtonBottom label="Back" />
    </div>
  );
}